/**
 * Dev-only component to visualize all Bundle Upload phase states.
 * Not used in production — purely for development / design review.
 *
 * Phases: UploadPhase → PollingPhase → ResultPhase
 *
 * Usage:
 *   import { BundleUploadStepPreview } from "./__mocks__/BundleUploadStepPreview";
 *   <BundleUploadStepPreview />
 */
import { AlertTriangle, CheckCircle2, XCircle } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";

import { FilePreview } from "../FilePreview";
import { ALL_MOCK_STEPS, createMockFile } from "./mockData";

const mockFile = createMockFile();

const AlertIcon = ({ variant }: { variant: string }) => {
  if (variant === "success") return <CheckCircle2 />;
  if (variant === "warning")
    return <AlertTriangle className="mt-0.5 text-warning" />;
  return <XCircle />;
};

export const BundleUploadStepPreview = () => {
  return (
    <div className="mx-auto grid max-w-5xl grid-cols-1 gap-6 p-8 md:grid-cols-2 lg:grid-cols-3">
      {ALL_MOCK_STEPS.map((step, i) => (
        <div key={i} className="space-y-2">
          <p className="text-xs font-medium text-foreground-muted">
            {step.description}
          </p>
          <Card className="gap-2 border p-2">
            <FilePreview
              file={mockFile}
              isSuccess={step.filePreview.isSuccess}
              isFailed={step.filePreview.isFailed}
              isLoading={
                "isLoading" in step.filePreview ? step.filePreview.isLoading : false
              }
              uploadProgress={step.filePreview.uploadProgress}
              onClear={
                "onClear" in step.filePreview ? step.filePreview.onClear : undefined
              }
            />

            {"alert" in step && step.alert && (
              <Alert variant={step.alert.variant} className="max-w-lg">
                <AlertIcon variant={step.alert.variant} />
                <AlertDescription>{step.alert.message}</AlertDescription>
              </Alert>
            )}

            {"progress" in step && step.progress && (
              <div className="space-y-1.5">
                <Progress
                  className="h-2"
                  value={"value" in step.progress ? step.progress.value : undefined}
                />
                <p className="text-center text-xs text-foreground-muted">
                  {step.progress.label}
                </p>
              </div>
            )}

            {"statusText" in step && (
              <>
                <p className="text-center text-xs text-foreground-muted">
                  {step.statusText}
                </p>
                {"pollingMessage" in step && (
                  <p className="mt-2 text-center text-xs text-foreground-muted">
                    {step.pollingMessage}
                  </p>
                )}
              </>
            )}

            {"button" in step && step.button && (
              <Button
                disabled={"disabled" in step.button ? step.button.disabled : false}
                isLoading={
                  "loading" in step.button ? Boolean(step.button.loading) : false
                }
                className={
                  "loading" in step.button && step.button.loading
                    ? "disabled:border-primary disabled:bg-primary"
                    : ""
                }
              >
                {step.button.text}
              </Button>
            )}
          </Card>
        </div>
      ))}
    </div>
  );
};
